/**
 * LF360-2024 — which permission opens which portal screen.
 *
 * One list for the menus and the route guards both, so a screen cannot be in a
 * menu that its route then refuses, or the other way round. A screen opens
 * when the user holds any of its keys; the keys are the ones the server
 * checks on the screen's own requests, so the portal never offers a screen
 * the server would answer with 403.
 *
 * Paths are without the project code. The longest matching prefix wins. A
 * path that is not listed here does not open behind `RequireScreen` — a new
 * screen has to be added before anybody can reach it.
 *
 * `access` is what `useAccess` returns: { isOrgAdmin, isProjectAdmin, permissions }.
 */

// Organization and System admins only: their screens are not project
// permissions (draft: those roles are not managed on the roles screen).
export const ORG_ADMIN = "orgAdmin";

// Sites have no catalog key (LF360-2037 gave them to Project admins by role);
// the server checks the role on these requests too.
export const PROJECT_ADMIN_ROLE = "projectAdminRole";

const SETTINGS_KEYS = [
  "settings.general",
  "settings.profile.view",
  "settings.profile.edit",
  "settings.invitation",
  "settings.cohorts",
  "settings.features",
  "settings.legal",
];

export const SCREENS = [
  ["/administration/system-users", ORG_ADMIN],
  ["/administration/disabled-users", ORG_ADMIN],
  ["/administration/pending-users", ORG_ADMIN],
  ["/administration/authentication", ORG_ADMIN],
  ["/administration/roles", ORG_ADMIN],
  // General settings is where every project setting starts.
  ["/administration/project-settings", SETTINGS_KEYS],
  ["/administration/profiles", ["settings.profile.view", "settings.profile.edit"]],
  ["/administration/enrollment", ["settings.invitation"]],
  ["/administration/cohorts", ["settings.cohorts"]],
  ["/administration/functionality", ["settings.features"]],
  ["/administration/sites", PROJECT_ADMIN_ROLE],
  ["/administration/notifications", ["settings.features"]],
  ["/administration/lifepoints", ["settings.features"]],
  ["/administration/mobile-home-page", ["settings.features"]],
  ["/administration/lifebot", ["settings.features"]],
  ["/administration/legal-documents", ["settings.legal"]],
  ["/administration/project-users", ["users.view"]],
  // LF360-2100: whether AI assistants may work in the project.
  ["/administration/project-ai-connectors", ORG_ADMIN],

  // A prefix that is not a page itself names, third, the page to land on.
  ["/content", ["content.view"], "/content/overview"],

  ["/analytics/audit-trail", ["analytics.audit"], "/analytics/audit-trail/subjects"],
  ["/analytics/monitoring", ["analytics.monitoring"], "/analytics/monitoring/scenarios"],
  ["/analytics/usage", ["analytics.monitoring"]],
  ["/analytics/reports", ["analytics.reports"], "/analytics/reports/surveys"],
  ["/analytics/ai-assistant", ["ai.reports"]],
];

const normalise = (path = "") =>
  String(path).split(/[?#]/)[0].replace(/\/+$/, "").toLowerCase();

/** The rule of the screen at `path` (without the project code), or undefined. */
export const screenRule = (path) => {
  const target = normalise(path);
  let found;
  for (const [prefix, rule] of SCREENS) {
    const candidate = prefix.toLowerCase();
    const matches = target === candidate || target.startsWith(`${candidate}/`);
    if (matches && (!found || candidate.length > found[0].length)) {
      found = [candidate, rule];
    }
  }
  return found?.[1];
};

/** Whether the user holds any of `keys`. */
export const holdsAny = (access, keys) =>
  access.isOrgAdmin || keys.some((key) => access.permissions.includes(key));

/** Whether the user may open the screen at `path` in the current project. */
export const canOpen = (access, path) => {
  const rule = screenRule(path);
  if (!rule) return false;
  if (rule === ORG_ADMIN) return access.isOrgAdmin;
  if (rule === PROJECT_ADMIN_ROLE) return access.isOrgAdmin || access.isProjectAdmin;
  return holdsAny(access, rule);
};

/** `items` (antd menu items, groups with children) without the screens the user cannot open. */
export const visibleItems = (access, items) =>
  items
    .map((item) => {
      if (item.children) {
        const children = visibleItems(access, item.children);
        return children.length ? { ...item, children } : null;
      }
      return canOpen(access, item.key) ? item : null;
    })
    .filter(Boolean);

const inSection = (section) => ([prefix]) =>
  prefix === section || prefix.startsWith(`${section}/`);

/**
 * The first screen of `section` (e.g. "/administration") the user may open,
 * in the order of SCREENS, `preferred` first when they may; null when none.
 */
export const firstOpenScreen = (access, section, preferred) => {
  if (preferred && canOpen(access, preferred)) return preferred;
  const screen = SCREENS.filter(inSection(section)).find(([prefix]) => canOpen(access, prefix));
  return screen ? screen[2] || screen[0] : null;
};

/** Whether the user may open any screen of `section`. */
export const canOpenSection = (access, section) =>
  firstOpenScreen(access, section) !== null;
